import { z } from 'zod';

/**
 * The extraction schema. This is passed to the model as a JSON schema via
 * `output_config.format`, and used to validate what comes back.
 *
 * Note what is NOT in here: citations. Citations come from the API's citation
 * mechanism, resolved against blocks we segmented ourselves. A model asked to
 * write its own citations into JSON will invent them.
 */

export const ITEM_TYPES = [
  'obligation',
  'kpi',
  'deadline',
  'funding',
  'eligibility',
  'reporting',
  'definition',
] as const;

export type ItemType = (typeof ITEM_TYPES)[number];

export const ITEM_TYPE_LABELS: Record<ItemType, string> = {
  obligation: 'Obligation',
  kpi: 'KPI',
  deadline: 'Deadline',
  funding: 'Funding',
  eligibility: 'Eligibility',
  reporting: 'Reporting',
  definition: 'Definition',
};

export const CONFIDENCE = ['high', 'medium', 'low'] as const;
export type Confidence = (typeof CONFIDENCE)[number];

export const REVIEW_STATUS = ['pending', 'accepted', 'edited', 'rejected'] as const;
export type ReviewStatus = (typeof REVIEW_STATUS)[number];

/** Only these two statuses may flow downstream to Ask, exports or comms. */
export const USABLE_STATUSES: ReviewStatus[] = ['accepted', 'edited'];

export const extractedItemSchema = z.object({
  type: z.enum(ITEM_TYPES),
  title: z.string().min(3).max(200),
  detail: z.string().min(1).max(4000),
  clauseReference: z.string().max(60).nullable(),
  appliesTo: z.string().max(200).nullable(),
  dueDate: z.string().max(60).nullable(),
  frequency: z.string().max(60).nullable(),
  value: z.number().nullable(),
  unit: z.string().max(40).nullable(),
  confidence: z.enum(CONFIDENCE),
  /*
   * There is deliberately no `sourceBlocks` field.
   *
   * There was one, described as "the model's own claim about which blocks it
   * used, kept so we can flag disagreement" with the real citations. The
   * cross-check was never built, and it could not have worked: the structuring
   * pass is sent no document at all — only the prose from pass one, which
   * carries no block numbers — so the model was being asked to produce indices
   * for something it cannot see. It had nothing to answer with but invention,
   * which is the exact failure the two-pass design exists to prevent.
   *
   * It was also required, so an item where the model sensibly returned nothing
   * failed validation and was dropped. A correct item, discarded for declining
   * to guess.
   *
   * Citations from pass one are the anchors, and they are the only ones.
   */
});

export type ExtractedItemDraft = z.infer<typeof extractedItemSchema>;

/**
 * Deliberately no whole-result schema.
 *
 * There was one, and validating the array in a single call meant one malformed
 * item threw away the entire extraction — on a large contract, five hundred
 * good items lost to a single short title. Structured outputs guarantee the
 * shape but not the content constraints (the API's JSON Schema support
 * excludes string lengths and numeric bounds), so that case is routine rather
 * than exotic.
 *
 * ai/extract.ts validates item by item with `extractedItemSchema` and reports
 * what it dropped. Reintroducing an all-or-nothing parse here would quietly
 * undo that.
 */

/**
 * The JSON Schema handed to `output_config.format`. Derived by hand rather
 * than generated so the descriptions can carry instruction to the model —
 * they materially improve extraction quality.
 */
export const EXTRACTION_JSON_SCHEMA = {
  type: 'object',
  properties: {
    items: {
      type: 'array',
      description:
        'Every distinct contractual item in the document. Do not merge two obligations into one item, and do not split a single obligation across two items.',
      items: {
        type: 'object',
        properties: {
          type: {
            type: 'string',
            enum: [...ITEM_TYPES],
            description:
              'obligation: something the provider must do. kpi: a measured indicator with a threshold or target. deadline: a dated requirement. funding: a payment, rate or financial condition. eligibility: which patients or organisations are in scope. reporting: what must be submitted, to whom, how often. definition: a defined term the rest of the document depends on.',
          },
          title: {
            type: 'string',
            description: 'A short factual label. Not a summary sentence. No more than about 12 words.',
          },
          detail: {
            type: 'string',
            description:
              'What the document actually requires, in the document\'s own terms. Do not add interpretation, advice or context that is not present in the source.',
          },
          clauseReference: {
            type: ['string', 'null'],
            description: 'The clause or indicator code if the document uses one, e.g. "DM037" or "7.4.2". Null if the document does not number this passage.',
          },
          appliesTo: {
            type: ['string', 'null'],
            description: 'Who this binds — e.g. "PCN", "each member practice", "the provider". Null if unstated.',
          },
          dueDate: { type: ['string', 'null'], description: 'As written in the document. Do not normalise or infer a year.' },
          frequency: { type: ['string', 'null'], description: 'e.g. "quarterly", "annually". Null if not recurring.' },
          value: { type: ['number', 'null'], description: 'The numeric threshold, target or amount, if there is exactly one.' },
          unit: { type: ['string', 'null'], description: 'e.g. "%", "£", "patients", "mmol/mol".' },
          confidence: {
            type: 'string',
            enum: [...CONFIDENCE],
            description:
              'low if the passage is ambiguous, contradicted elsewhere in the document, or you are inferring rather than reading. Be honest — low-confidence items are sorted to the top of the human review queue, which is where they belong.',
          },
        },
        required: [
          'type', 'title', 'detail', 'clauseReference', 'appliesTo',
          'dueDate', 'frequency', 'value', 'unit', 'confidence',
        ],
        additionalProperties: false,
      },
    },
    documentSummary: {
      type: 'string',
      description: 'Two or three sentences on what this document is and who it binds. Factual only.',
    },
    unparsedSections: {
      type: 'array',
      items: { type: 'string' },
      description:
        'Sections you could not reliably parse — tables that did not survive text extraction, passages that appear truncated, or content that contradicts itself. Naming them is more useful than guessing at them.',
    },
  },
  required: ['items', 'documentSummary', 'unparsedSections'],
  additionalProperties: false,
} as const;
